import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { CloseCircle } from 'asset';
import { LoadingPopup } from 'components/common';
import { DatePickerField, Inputs, Select } from 'components/form-control';
import yup from 'config/yup.custom';
import { NotifyService } from 'helpers';
import { OptionItem, STATUSES } from 'models';
import {
  CREATE_PUBLIC_HOLIDAY_PAYLOAD,
  PUBLIC_HOLIDAY_ITEM_TYPE,
  UPDATE_PUBLIC_HOLIDAY_PAYLOAD,
} from 'models/public-holiday';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  createPublicHolidayApi,
  updatePublicHolidayApi,
} from 'services/public-holidays';
import { PUBLIC_HOLIDAY_STATUSES } from '../../data';

interface PopupCreateEditPHProps {
  open: boolean;
  subject?: PUBLIC_HOLIDAY_ITEM_TYPE;
  onSuccess: () => void;
  handleClosePopup: () => void;
}

interface IFormInputs {
  name: string;
  date: string;
  status?: OptionItem;
}

const defaultValues: Partial<IFormInputs> = {
  name: '',
  date: '',
  status: undefined,
};

export const PopupCreateEditPH = (props: PopupCreateEditPHProps) => {
  const { open, handleClosePopup, onSuccess, subject } = props;

  const [loadingPopup, setLoadingPopup] = useState(false);

  const theme = useTheme();

  const schema = useMemo(() => {
    return yup.object({
      name: yup.string().required('Name is required'),
      date: yup.string().required('Date is required'),
      status: subject
        ? yup.mixed().required('Status is required')
        : yup.mixed().notRequired(),
    });
  }, [subject]);

  const form = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: defaultValues,
  });

  const { reset } = form;

  const _handleClosePopup = () => {
    handleClosePopup();
    reset({ ...defaultValues });
  };

  useEffect(() => {
    if (!open) return;
    if (subject) {
      reset({
        name: subject.name,
        date: subject.date,
        status: PUBLIC_HOLIDAY_STATUSES.find((item) =>
          subject.is_active
            ? item.id === STATUSES.ACTIVE
            : item.id === STATUSES.INACTIVE
        ),
      });
    }
  }, [open, subject, reset]);

  const getDateTime = (dateString: string) => {
    if (dateString.includes('-')) {
      // Split the date string into parts
      // For example: ["2025", "07", "16"]
      const [month, day, year] = dateString.split('-');
      return {
        date: `${month}-${day}`,
        year: Number(year),
      };
    } else {
      // Split the date string into parts
      // For example: ["Wed", "Jul", "16", "2025", ...]
      const dateSplit = dateString.split(' ');

      // Get the day from the date string
      const dateStr = dateSplit[2];

      // Get the month from the date string
      const monthStr = dateSplit[1];

      // Get the year from the date string
      const yearStr = dateSplit[3];

      // Create a mapping of month names to their corresponding numbers
      // Note: months are 0-indexed in JavaScript, so January is 0 and December is 11
      const monthMap = {
        Jan: '01',
        Feb: '02',
        Mar: '03',
        Apr: '04',
        May: '05',
        Jun: '06',
        Jul: '07',
        Aug: '08',
        Sep: '09',
        Oct: '10',
        Nov: '11',
        Dec: '12',
      };
      const month = monthMap[monthStr as keyof typeof monthMap];

      // Format the date as "month - day"
      const dateFormatted = `${month}-${dateStr?.padStart(2, '0')}`;
      return {
        date: dateFormatted,
        year: Number(yearStr),
      };
    }
  };

  const onSubmit = async (values: IFormInputs) => {
    const customValues:
      | CREATE_PUBLIC_HOLIDAY_PAYLOAD
      | UPDATE_PUBLIC_HOLIDAY_PAYLOAD = {
      date: getDateTime(values.date).date,
      year: getDateTime(values.date).year,
      name: values.name,
      is_fixed: false,
      is_active: values.status?.id === STATUSES.ACTIVE,
    };

    setLoadingPopup(true);

    await (subject
      ? updatePublicHolidayApi(
          subject.id,
          customValues as UPDATE_PUBLIC_HOLIDAY_PAYLOAD
        )
      : createPublicHolidayApi(customValues as CREATE_PUBLIC_HOLIDAY_PAYLOAD)
    )
      .then((r) => {
        onSuccess();
        _handleClosePopup();
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {
        setLoadingPopup(false);
      });
  };
  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          width: 527,
          borderRadius: 0,
        },
      }}
    >
      <Grid>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 25px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
            }}
          >
            {subject ? 'Edit' : 'Create'} Public Holiday
          </Typography>
          <IconButton
            onClick={_handleClosePopup}
            sx={{
              padding: 0,
            }}
          >
            <CloseCircle />
          </IconButton>
        </Grid>
        <Divider />
        {loadingPopup && <LoadingPopup color="primary" />}

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack spacing="20px" p="25px 24px">
            <Stack>
              <Inputs
                title="Public Holiday Name"
                form={form}
                name="name"
                placeholder="Enter public holiday name"
              />
            </Stack>
            <Stack
              sx={{
                width: '50%',
              }}
            >
              <DatePickerField title="Date" form={form} name="date" />
            </Stack>
            <Stack>
              <Select
                title="Status"
                name="status"
                bindLabel="name"
                form={form}
                filterFunc={true}
                options={PUBLIC_HOLIDAY_STATUSES}
              />
            </Stack>
            <Stack direction="row" spacing="8px">
              <Button
                variant="outlined"
                color="secondary"
                onClick={_handleClosePopup}
                sx={{
                  height: 30,
                  width: '100%',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: 1.1,
                  border: `1px solid ${theme.palette.secondary.main}`,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                sx={{
                  height: 30,
                  width: '100%',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: 1.1,
                }}
              >
                {subject ? 'Update' : 'Create'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Grid>
    </Dialog>
  );
};

export default PopupCreateEditPH;
